/**
 * Backend wake + health check for "Get started" / login flow.
 * Triggers PM2 keepalive (wake) then polls health until backend responds or timeout.
 */

import { API_ROOT } from '@/config/apiBase';

const WAKE_URL = `${API_ROOT}/internal/wake`;
const HEALTH_URL = `${API_ROOT}/health/liveness`;

const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 60000; // 60s

/**
 * Call the rate-limited wake endpoint to trigger PM2 keepalive (resurrect + save).
 * No secret required; safe to call from frontend.
 */
export async function triggerWake(): Promise<void> {
  try {
    await fetch(WAKE_URL, { method: 'GET', mode: 'cors' });
  } catch {
    // Ignore: backend may be down; health poll will retry
  }
}

/**
 * Poll health until 200 or timeout. Returns true if backend responded, false if timeout.
 */
export async function waitForBackend(): Promise<boolean> {
  const deadline = Date.now() + MAX_WAIT_MS;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(HEALTH_URL, { method: 'GET', mode: 'cors' });
      if (res.ok) return true;
    } catch {
      // Not ready yet
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return false;
}

/**
 * Trigger wake (keepalive) then poll health until backend is up or timeout.
 * Use when user clicks "Get started" or before showing sign-in/sign-up form.
 */
export async function ensureBackendUp(): Promise<boolean> {
  await triggerWake();
  return waitForBackend();
}
